import { AlertCircle, Download, FileSpreadsheet } from 'lucide-react';

function ResourceCard({ resource }) {
  const { title, description, chapter, icon, file, fileType, fileSize, tags } = resource;

  return (
    <div className="group bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/40 hover:border-emerald-500/30 rounded-2xl p-6 transition-all flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-amber-500/10 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
            {icon}
          </div>
          <div>
            <h3 className="text-white font-semibold leading-tight">{title}</h3>
            <span className="text-amber-400/70 text-xs">{chapter}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-slate-500 text-xs flex-shrink-0">
          <FileSpreadsheet size={12} />
          <span>{fileType}</span>
        </div>
      </div>

      {/* Description */}
      <p className="text-slate-400 text-sm leading-relaxed flex-1 mb-4">{description}</p>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tags.map(tag => (
          <span key={tag} className="bg-slate-700/50 text-slate-400 text-xs px-2.5 py-0.5 rounded-full">
            {tag}
          </span>
        ))}
      </div>

      {/* Download button */}
      <a
        href={file}
        download
        className="flex items-center justify-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-400 hover:text-emerald-300 font-semibold text-sm px-4 py-2.5 rounded-xl transition-all group-hover:shadow-lg group-hover:shadow-emerald-500/5"
      >
        <Download size={15} />
        Download Free ({fileSize})
      </a>
    </div>
  );
}

export default function ResourcesSection({ resources }) {
  return (
    <div className="bg-gradient-to-b from-[#0d1e35] to-[#0A1628] py-24 px-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
            <Download size={13} className="text-emerald-400" />
            <span className="text-emerald-300 text-xs font-semibold tracking-wide uppercase">Free Companion Resources</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Your Digital Toolkit
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Every major exercise in the book has a companion Excel tool. Download them free — no account required. These are the same templates referenced throughout <span className="text-amber-300 font-medium">Wealth At Any Age</span>.
          </p>
        </div>

        {/* Resource grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-12">
          {resources.map(r => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>

        {/* How to use callout */}
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-8 max-w-3xl mx-auto text-center">
          <h3 className="text-white font-bold text-lg mb-3">How to use these tools</h3>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            Open each file in Microsoft Excel or Google Sheets (File → Import). Yellow cells are your inputs — everything else calculates automatically. Start with the <strong className="text-amber-300">Wealth Assessment Workbook</strong> to establish your baseline, then use the relevant tool for the chapter you're working through.
          </p>
          <div className="flex items-start gap-2 bg-slate-800/40 rounded-xl p-4 text-left">
            <AlertCircle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-slate-500 text-xs leading-relaxed">
              These tools are for educational purposes only and do not constitute financial advice. Results depend on your individual circumstances. Consult a qualified financial advisor before making investment decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
